import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

const AllFlightDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [flight, setFlight] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedFare, setSelectedFare] = useState('economy');

  useEffect(() => {
    const fetchFlightDetails = async () => {
      setLoading(true);
      try {
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1500));

        // Mock detailed flight data
        const mockFlight = {
          id: parseInt(id),
          flightNumber: 'JS 1234',
          airline: 'JetScanner Airways',
          aircraft: {
            model: 'Boeing 737-800',
            registration: 'N123JS',
            age: '5 years',
            configuration: '3-3',
            totalSeats: 189
          },
          route: {
            departure: {
              airport: 'JFK',
              airportName: 'John F. Kennedy International Airport',
              city: 'New York',
              country: 'United States',
              terminal: 'Terminal 4',
              gate: 'A12',
              time: '08:30',
              date: '2024-03-20',
              timezone: 'EST'
            },
            arrival: {
              airport: 'LAX',
              airportName: 'Los Angeles International Airport',
              city: 'Los Angeles',
              country: 'United States',
              terminal: 'Terminal 1',
              gate: 'B8',
              time: '11:45',
              date: '2024-03-20',
              timezone: 'PST'
            },
            duration: '5h 15m',
            distance: '2,475 miles',
            stops: 0
          },
          fares: {
            economy: {
              name: 'Economy',
              price: 450,
              available: 23,
              features: [
                'Standard seat',
                '1 x 23kg checked bag',
                'Meal included',
                'In-flight entertainment',
                'Free WiFi'
              ],
              restrictions: [
                'Changes allowed with fee',
                'Refundable with penalty',
                'Seat selection for fee'
              ]
            },
            premium: {
              name: 'Premium Economy',
              price: 680,
              available: 12,
              features: [
                'Extra legroom seat',
                '2 x 23kg checked bags',
                'Premium meal',
                'Priority boarding',
                'In-flight entertainment',
                'Free WiFi',
                'Complimentary drinks'
              ],
              restrictions: [
                'Changes allowed with reduced fee',
                'Refundable with reduced penalty',
                'Free seat selection'
              ]
            },
            business: {
              name: 'Business Class',
              price: 1250,
              available: 8,
              features: [
                'Lie-flat seat',
                '2 x 32kg checked bags',
                'Gourmet meal',
                'Priority check-in & boarding',
                'Lounge access',
                'In-flight entertainment',
                'Free WiFi',
                'Premium drinks & amenities'
              ],
              restrictions: [
                'Free changes',
                'Fully refundable',
                'Free seat selection',
                'Free upgrades when available'
              ]
            }
          },
          amenities: {
            aircraft: [
              { name: 'WiFi', available: true, cost: 'Free' },
              { name: 'Power Outlets', available: true, cost: 'Free' },
              { name: 'USB Ports', available: true, cost: 'Free' },
              { name: 'Entertainment System', available: true, cost: 'Free' },
              { name: 'Overhead Storage', available: true, cost: 'Free' }
            ],
            services: [
              { name: 'Meals', available: true, cost: 'Included' },
              { name: 'Beverages', available: true, cost: 'Included' },
              { name: 'Blanket & Pillow', available: true, cost: 'Free' },
              { name: 'Priority Boarding', available: true, cost: '$25' },
              { name: 'Extra Baggage', available: true, cost: '$50/bag' }
            ]
          },
          baggage: {
            carryOn: {
              included: 1,
              weight: '10kg',
              dimensions: '56x45x25cm',
              additional: '$35 per bag'
            },
            checked: {
              economy: { included: 1, weight: '23kg', additional: '$50 per bag' },
              premium: { included: 2, weight: '23kg', additional: '$40 per bag' },
              business: { included: 2, weight: '32kg', additional: '$30 per bag' }
            }
          },
          policies: {
            cancellation: {
              economy: 'Refundable with $100 penalty up to 24h before departure',
              premium: 'Refundable with $50 penalty up to 2h before departure',
              business: 'Fully refundable up to departure time'
            },
            changes: {
              economy: 'Changes allowed with $75 fee + fare difference',
              premium: 'Changes allowed with $25 fee + fare difference',
              business: 'Free changes allowed'
            }
          },
          onTimePerformance: 87,
          averageDelay: '12 minutes',
          weatherForecast: {
            departure: { condition: 'Sunny', temp: '22°C', wind: '10 mph' },
            arrival: { condition: 'Partly Cloudy', temp: '26°C', wind: '8 mph' }
          }
        };

        setFlight(mockFlight);
      } catch (error) {
        console.error('Error fetching flight details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFlightDetails();
  }, [id]);

  const handleBookFlight = () => {
    const selectedFareDetails = flight.fares[selectedFare];
    navigate(`/select-seat/${flight.id}`, {
      state: {
        flight,
        fare: selectedFareDetails,
        fareType: selectedFare,
        searchCriteria: location.state?.searchCriteria
      }
    });
  };

  const formatDateTime = (date, time, timezone) => {
    return `${new Date(date).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    })} ${time} ${timezone}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-jet-gray-50 pt-[70px]">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-jet-yellow mx-auto mb-4"></div>
            <p className="text-jet-gray-600">Loading flight details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!flight) {
    return (
      <div className="min-h-screen bg-jet-gray-50 pt-[70px]">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-jet-red-100 rounded-full mb-4">
              <svg className="w-8 h-8 text-jet-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-jet-gray-900 mb-2">Flight Not Found</h2>
            <p className="text-jet-gray-600 mb-4">The flight details could not be loaded.</p>
            <button
              onClick={() => navigate(-1)}
              className="bg-jet-yellow hover:bg-jet-yellow-hover text-jet-gray-900 font-medium py-2 px-4 rounded-md transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-jet-gray-50 pt-[70px]">
      <main className="py-8">
        <div className="w-[86%] mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <button
                onClick={() => navigate(-1)}
                className="flex items-center text-jet-gray-600 hover:text-jet-gray-900 transition-colors mb-2"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
                </svg>
                Back to Results
              </button>
              <h1 className="text-2xl font-bold text-jet-gray-900">Flight Details</h1>
              <p className="text-jet-gray-600">{flight.flightNumber} • {flight.airline}</p>
            </div>

            <div className="text-right">
              <div className="flex items-center space-x-2 mb-2">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="text-sm text-jet-gray-600">On-time performance: {flight.onTimePerformance}%</span>
              </div>
              <p className="text-sm text-jet-gray-500">Average delay: {flight.averageDelay}</p>
            </div>
          </div>

          {/* Flight Overview */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <div className="grid md:grid-cols-3 gap-8 items-center">
              {/* Departure */}
              <div>
                <h3 className="text-lg font-semibold text-jet-gray-900 mb-2">Departure</h3>
                <p className="text-3xl font-bold text-jet-blue-600 mb-1">{flight.route.departure.time}</p>
                <p className="text-lg font-medium text-jet-gray-900">{flight.route.departure.airport}</p>
                <p className="text-sm text-jet-gray-600">{flight.route.departure.airportName}</p>
                <p className="text-sm text-jet-gray-600">{flight.route.departure.city}, {flight.route.departure.country}</p>
                <div className="mt-2 pt-2 border-t border-jet-gray-200">
                  <p className="text-sm text-jet-gray-600">
                    {formatDateTime(flight.route.departure.date, flight.route.departure.time, flight.route.departure.timezone)}
                  </p>
                  <p className="text-sm text-jet-gray-600">Terminal {flight.route.departure.terminal} • Gate {flight.route.departure.gate}</p>
                </div>
              </div>

              {/* Flight Info */}
              <div className="text-center">
                <div className="flex items-center justify-center mb-4">
                  <div className="flex-1 border-t border-jet-gray-300"></div>
                  <div className="px-4">
                    <svg className="w-8 h-8 text-jet-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"></path>
                    </svg>
                  </div>
                  <div className="flex-1 border-t border-jet-gray-300"></div>
                </div>
                <p className="text-lg font-semibold text-jet-gray-900 mb-1">{flight.route.duration}</p>
                <p className="text-sm text-jet-gray-600 mb-2">{flight.route.distance}</p>
                <p className="text-sm text-jet-gray-600">
                  {flight.route.stops === 0 ? 'Non-stop' : `${flight.route.stops} stop${flight.route.stops > 1 ? 's' : ''}`}
                </p>
                <div className="mt-3 pt-3 border-t border-jet-gray-200">
                  <p className="text-sm font-medium text-jet-gray-900">{flight.aircraft.model}</p>
                  <p className="text-xs text-jet-gray-600">{flight.aircraft.registration}</p>
                </div>
              </div>

              {/* Arrival */}
              <div className="text-right">
                <h3 className="text-lg font-semibold text-jet-gray-900 mb-2">Arrival</h3>
                <p className="text-3xl font-bold text-jet-blue-600 mb-1">{flight.route.arrival.time}</p>
                <p className="text-lg font-medium text-jet-gray-900">{flight.route.arrival.airport}</p>
                <p className="text-sm text-jet-gray-600">{flight.route.arrival.airportName}</p>
                <p className="text-sm text-jet-gray-600">{flight.route.arrival.city}, {flight.route.arrival.country}</p>
                <div className="mt-2 pt-2 border-t border-jet-gray-200">
                  <p className="text-sm text-jet-gray-600">
                    {formatDateTime(flight.route.arrival.date, flight.route.arrival.time, flight.route.arrival.timezone)}
                  </p>
                  <p className="text-sm text-jet-gray-600">Terminal {flight.route.arrival.terminal} • Gate {flight.route.arrival.gate}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-lg shadow-sm mb-6">
            <div className="border-b border-jet-gray-200">
              <nav className="flex space-x-8 px-6">
                {[
                  { id: 'overview', label: 'Overview', icon: '📋' },
                  { id: 'amenities', label: 'Amenities', icon: '✨' },
                  { id: 'baggage', label: 'Baggage', icon: '🧳' },
                  { id: 'policies', label: 'Policies', icon: '📄' }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === tab.id
                      ? 'border-jet-yellow text-jet-yellow'
                      : 'border-transparent text-jet-gray-500 hover:text-jet-gray-700 hover:border-jet-gray-300'
                      }`}
                  >
                    <span className="mr-2">{tab.icon}</span>
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>

            <div className="p-6">
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="text-lg font-semibold text-jet-gray-900 mb-4">Aircraft Information</h3>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-jet-gray-600">Model:</span>
                          <span className="text-jet-gray-900 font-medium">{flight.aircraft.model}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-jet-gray-600">Registration:</span>
                          <span className="text-jet-gray-900 font-medium">{flight.aircraft.registration}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-jet-gray-600">Age:</span>
                          <span className="text-jet-gray-900 font-medium">{flight.aircraft.age}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-jet-gray-600">Configuration:</span>
                          <span className="text-jet-gray-900 font-medium">{flight.aircraft.configuration}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-jet-gray-600">Available Seats:</span>
                          <span className="text-jet-gray-900 font-medium">{Object.values(flight.fares).reduce((acc, f) => acc + f.available, 0)}</span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold text-jet-gray-900 mb-4">Weather Forecast</h3>
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-medium text-jet-gray-900 mb-2">Departure ({flight.route.departure.city})</h4>
                          <div className="flex items-center space-x-4 text-sm">
                            <span className="text-jet-gray-600">{flight.weatherForecast.departure.condition}</span>
                            <span className="text-jet-gray-900 font-medium">{flight.weatherForecast.departure.temp}</span>
                            <span className="text-jet-gray-600">Wind: {flight.weatherForecast.departure.wind}</span>
                          </div>
                        </div>
                        <div>
                          <h4 className="font-medium text-jet-gray-900 mb-2">Arrival ({flight.route.arrival.city})</h4>
                          <div className="flex items-center space-x-4 text-sm">
                            <span className="text-jet-gray-600">{flight.weatherForecast.arrival.condition}</span>
                            <span className="text-jet-gray-900 font-medium">{flight.weatherForecast.arrival.temp}</span>
                            <span className="text-jet-gray-600">Wind: {flight.weatherForecast.arrival.wind}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Amenities Tab */}
              {activeTab === 'amenities' && (
                <div className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="text-lg font-semibold text-jet-gray-900 mb-4">Aircraft Amenities</h3>
                      <div className="space-y-3">
                        {flight.amenities.aircraft.map((amenity, index) => (
                          <div key={index} className="flex items-center justify-between">
                            <div className="flex items-center">
                              <div className={`w-3 h-3 rounded-full mr-3 ${amenity.available ? 'bg-green-500' : 'bg-red-500'}`}></div>
                              <span className="text-jet-gray-900">{amenity.name}</span>
                            </div>
                            <span className="text-sm text-jet-gray-600">{amenity.cost}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold text-jet-gray-900 mb-4">Services</h3>
                      <div className="space-y-3">
                        {flight.amenities.services.map((service, index) => (
                          <div key={index} className="flex items-center justify-between">
                            <div className="flex items-center">
                              <div className={`w-3 h-3 rounded-full mr-3 ${service.available ? 'bg-green-500' : 'bg-red-500'}`}></div>
                              <span className="text-jet-gray-900">{service.name}</span>
                            </div>
                            <span className="text-sm text-jet-gray-600">{service.cost}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Baggage Tab */}
              {activeTab === 'baggage' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-jet-gray-900 mb-4">Carry-on Baggage</h3>
                    <div className="bg-jet-blue-50 border border-jet-blue-200 rounded-lg p-4">
                      <div className="grid md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="font-medium text-jet-blue-900">Included:</span>
                          <p className="text-jet-blue-800">{flight.baggage.carryOn.included} bag</p>
                        </div>
                        <div>
                          <span className="font-medium text-jet-blue-900">Weight Limit:</span>
                          <p className="text-jet-blue-800">{flight.baggage.carryOn.weight}</p>
                        </div>
                        <div>
                          <span className="font-medium text-jet-blue-900">Dimensions:</span>
                          <p className="text-jet-blue-800">{flight.baggage.carryOn.dimensions}</p>
                        </div>
                      </div>
                      <p className="text-xs text-jet-blue-700 mt-2">Additional bags: {flight.baggage.carryOn.additional}</p>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-jet-gray-900 mb-4">Checked Baggage</h3>
                    <div className="space-y-4">
                      {Object.entries(flight.baggage.checked).map(([fareType, baggage]) => (
                        <div key={fareType} className="border border-jet-gray-200 rounded-lg p-4">
                          <h4 className="font-medium text-jet-gray-900 mb-2 capitalize">{fareType} Class</h4>
                          <div className="grid md:grid-cols-3 gap-4 text-sm">
                            <div>
                              <span className="font-medium text-jet-gray-700">Included:</span>
                              <p className="text-jet-gray-900">{baggage.included} bag{baggage.included > 1 ? 's' : ''}</p>
                            </div>
                            <div>
                              <span className="font-medium text-jet-gray-700">Weight Limit:</span>
                              <p className="text-jet-gray-900">{baggage.weight} each</p>
                            </div>
                            <div>
                              <span className="font-medium text-jet-gray-700">Additional:</span>
                              <p className="text-jet-gray-900">{baggage.additional}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Policies Tab */}
              {activeTab === 'policies' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-jet-gray-900 mb-4">Cancellation Policy</h3>
                    <div className="space-y-3">
                      {Object.entries(flight.policies.cancellation).map(([fareType, policy]) => (
                        <div key={fareType} className="border border-jet-gray-200 rounded-lg p-4">
                          <h4 className="font-medium text-jet-gray-900 mb-2 capitalize">{fareType} Class</h4>
                          <p className="text-sm text-jet-gray-700">{policy}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-jet-gray-900 mb-4">Change Policy</h3>
                    <div className="space-y-3">
                      {Object.entries(flight.policies.changes).map(([fareType, policy]) => (
                        <div key={fareType} className="border border-jet-gray-200 rounded-lg p-4">
                          <h4 className="font-medium text-jet-gray-900 mb-2 capitalize">{fareType} Class</h4>
                          <p className="text-sm text-jet-gray-700">{policy}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Fare Selection */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold text-jet-gray-900 mb-6">Select Your Fare</h2>

            <div className="grid md:grid-cols-3 gap-6">
              {Object.entries(flight.fares).map(([fareType, fare]) => (
                <div
                  key={fareType}
                  className={`border-2 rounded-lg p-6 cursor-pointer transition-colors ${selectedFare === fareType
                    ? 'border-jet-yellow bg-jet-yellow-50'
                    : 'border-jet-gray-200 hover:border-jet-gray-300'
                    }`}
                  onClick={() => setSelectedFare(fareType)}
                >
                  <div className="text-center mb-4">
                    <h3 className="text-lg font-semibold text-jet-gray-900 mb-2">{fare.name}</h3>
                    <p className="text-3xl font-bold text-jet-blue-600">${fare.price}</p>
                    <p className="text-sm text-jet-gray-600">per person</p>
                    <p className="text-xs text-jet-gray-500 mt-1">{fare.available} seats available</p>
                  </div>

                  <div className="space-y-2 mb-4">
                    <h4 className="font-medium text-jet-gray-900 text-sm">Included:</h4>
                    {fare.features.slice(0, 3).map((feature, index) => (
                      <div key={index} className="flex items-center text-sm">
                        <svg className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                        </svg>
                        <span className="text-jet-gray-700">{feature}</span>
                      </div>
                    ))}
                    {fare.features.length > 3 && (
                      <p className="text-xs text-jet-gray-500">+{fare.features.length - 3} more features</p>
                    )}
                  </div>

                  <div className="flex items-center justify-center">
                    <div className={`w-4 h-4 rounded-full border-2 ${selectedFare === fareType
                      ? 'border-jet-yellow bg-jet-yellow'
                      : 'border-jet-gray-300'
                      }`}>
                      {selectedFare === fareType && (
                        <div className="w-2 h-2 bg-white rounded-full m-0.5"></div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 text-center">
              <button
                onClick={handleBookFlight}
                className="bg-jet-yellow hover:bg-jet-yellow-hover text-jet-gray-900 font-semibold py-4 px-8 rounded-md transition-colors text-lg"
              >
                Continue with {flight.fares[selectedFare].name} - ${flight.fares[selectedFare].price}
              </button>
              <p className="text-sm text-jet-gray-600 mt-2">
                Price shown is per person. Taxes and fees included.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AllFlightDetailsPage;